import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";

// Reusable slide-from-right drawer shell. Used for add / edit / info everywhere
// (no modals for those). Sticky header + scrollable body + sticky footer.
export function Drawer({ open, onClose, title, subtitle, icon, iconTone = "brand", children, toolbar, footer, width = 480 }) {
  const t = useDashTheme();

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const tones = {
    brand: [t.c.primary, t.c.primarySoft],
    warning: [t.status.warning, t.status.warningSoft],
    danger: [t.status.negative, t.status.negativeSoft],
    ai: [t.c.ai, t.c.aiSoft],
  };
  const [iconColor, iconBg] = tones[iconTone] || tones.brand;

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(8,10,18,0.5)", backdropFilter: "blur(3px)" }}
          />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={t.motion.spring.smooth}
            style={{
              position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 9999, width: `min(${width}px, 100vw)`,
              background: t.c.surface, borderLeft: `1px solid ${t.c.border}`, boxShadow: t.shadow.lg,
              display: "flex", flexDirection: "column", fontFamily: t.font.family,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: `${t.s[5]}px ${t.s[5]}px ${t.s[4]}px`, borderBottom: `1px solid ${t.c.border}`, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                {icon && (
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: 11, background: iconBg, color: iconColor, flexShrink: 0 }}>{icon}</span>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
                  {subtitle && <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
                </div>
              </div>
              <button onClick={onClose} style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "none", background: t.c.surfaceMuted, color: t.c.textSecondary, cursor: "pointer", flexShrink: 0 }}>
                <X size={16} />
              </button>
            </div>

            {toolbar && (
              <div style={{ flexShrink: 0, padding: `${t.s[4]}px ${t.s[5]}px`, borderBottom: `1px solid ${t.c.border}`, background: t.c.surface }}>
                {toolbar}
              </div>
            )}

            <div style={{ flex: 1, overflowY: "auto", padding: t.s[5] }}>{children}</div>

            {footer && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: t.s[2], padding: `${t.s[4]}px ${t.s[5]}px`, borderTop: `1px solid ${t.c.border}`, flexShrink: 0, background: t.c.surface }}>
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
